import React, { useState } from "react";
import { Play, X, Youtube } from "lucide-react";
import { useCMS, useSectionEnabled } from "../context/CMSContext";

export default function WallOfLove() {
  const enabled = useSectionEnabled("wallOfLove");
  const { cms } = useCMS();
  const [active, setActive] = useState(null);
  if (!enabled) return null;
  const vids = cms?.wallOfLove || [];

  const Card = ({ v, compact }) => (
    <button
      onClick={() => setActive(v.youtubeId)}
      className={`lm-card overflow-hidden text-left group ${compact ? "w-[280px] flex-shrink-0" : ""}`}
      style={compact ? { scrollSnapAlign: "start" } : {}}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={v.thumb}
          alt={v.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className={`${compact ? "w-12 h-12" : "w-14 h-14"} rounded-full bg-[#FF6B3D] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
            <Play className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-[#11151C] ml-1 fill-[#11151C]`} />
          </span>
        </span>
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-white text-[10px] sm:text-xs bg-black/40 backdrop-blur px-1.5 py-0.5 rounded">
          <Youtube className="w-3 h-3" /> YouTube
        </span>
      </div>
      <div className={compact ? "p-4" : "p-5"}>
        <h4 className={`lm-font font-bold text-[#11151C] line-clamp-2 group-hover:text-[#FF6B3D] transition-colors ${compact ? "text-sm" : ""}`}>
          {v.title}
        </h4>
        <p className={`text-[#11151C]/60 mt-1.5 ${compact ? "text-xs" : "text-sm"}`}>{v.channel}</p>
      </div>
    </button>
  );

  return (
    <section className="bg-[#FFFBF6] relative py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12">
          <span className="lm-chip-accent inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase">
            Wall of Love
          </span>
          <h2 className="lm-section-title mt-4 text-2xl sm:text-4xl lg:text-5xl">
            Don't just take our word for it
          </h2>
          <p className="text-[#11151C]/65 mt-3 text-base sm:text-lg">
            See why top creators and agencies are switching to {cms?.site?.brandName || "Live Mover"}.
          </p>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5">
          {vids.map((v) => (
            <Card key={v.youtubeId} v={v} />
          ))}
        </div>

        {/* Mobile horizontal scroll */}
        <div
          className="md:hidden -mx-4 px-4 overflow-x-auto pb-3"
          style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex gap-4" style={{ width: "max-content" }}>
            {vids.map((v) => (
              <Card key={v.youtubeId} v={v} compact />
            ))}
          </div>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setActive(null)}
              className="absolute -top-12 right-0 text-white hover:text-[#FF6B3D] transition-colors"
              aria-label="Close"
            >
              <X className="w-7 h-7" />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${active}?autoplay=1`}
              title="YouTube video"
              className="w-full h-full rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
